import { UnreachableCaseError } from '@oberoninternal/travelbase-ds/entities/UnreachableCaseError';
import differenceInDays from 'date-fns/differenceInDays';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import Link from 'next/link';
import { FormattedMessage } from 'react-intl';
import { BookingStatus, MyOrderFragment, OrderItemFragment, OrderItem_Booking_Fragment } from '../generated/graphql';
import parseDateTime from './parseDateTime';

export const declinedStatuses: BookingStatus[] = [
    BookingStatus.Declined,
    BookingStatus.CancelledGuest,
    BookingStatus.CancelledPartner,
    BookingStatus.CancelledExternal,
    BookingStatus.Waived,
];

export const isUpcoming = (item: OrderItemFragment) => {
    let dateToCompare: Date | string = new Date();
    if (item.__typename) {
        switch (item.__typename) {
            case 'Booking':
                dateToCompare = item.arrivalDate;
                break;
            case 'Ticket': {
                if (!item.timeslot) {
                    return false;
                }
                dateToCompare = parseDateTime(item.timeslot.startDateTime);
                break;
            }
            // TODO: implement UpsellPurchase status
            case 'UpsellPurchase':
                return false;
            default:
                throw new UnreachableCaseError(item.__typename);
        }
    }
    return isAfter(new Date(dateToCompare), new Date());
};
export const isDone = (item: OrderItem_Booking_Fragment) => isBefore(new Date(item.departureDate), new Date());
export const isReviewable = (item: OrderItem_Booking_Fragment) => !item.review;
export const isRecent = (item: OrderItem_Booking_Fragment) =>
    differenceInDays(new Date(), new Date(item.departureDate)) < 60;
export const isDeclined = (item: OrderItemFragment) => {
    if (!item.__typename) {
        return true;
    }
    switch (item.__typename) {
        case 'Booking':
            return declinedStatuses.includes(item.status);
        case 'Ticket':
            // TODO: can a ticket be declined? Most probably, but it isn't supported yet.
            return false;
        // TODO: implement UpsellPurchase status
        case 'UpsellPurchase':
            return false;
        default:
            throw new UnreachableCaseError(item.__typename);
    }
};

export const getOrderTitle = (order: MyOrderFragment, locale: string) => {
    const mainBooking = order.orderItems.find(
        (item): item is OrderItem_Booking_Fragment => item.__typename === 'Booking'
    );

    if (!mainBooking) {
        return (
            <FormattedMessage
                defaultMessage="Reservering ({orderId})"
                values={{
                    orderId: order.id ? (
                        <Link passHref href={`/my/order/${order.id}`} legacyBehavior>
                            {order.number ?? 1}
                        </Link>
                    ) : (
                        ''
                    ),
                }}
            />
        );
    }

    switch (mainBooking?.status) {
        case 'ACCEPTED':
        case 'NO_SHOW':
            return (
                <FormattedMessage
                    defaultMessage="Reservering ({orderId})"
                    values={{
                        orderId: order.id ? (
                            <Link passHref href={`/${locale}/my/order/${order.id}`} legacyBehavior>
                                {/* An accepted, no_show booking should actually always have a number */}
                                {order.number ?? 1}
                            </Link>
                        ) : (
                            ''
                        ),
                    }}
                />
            );
        case 'DECLINED':
            return <FormattedMessage defaultMessage="Afgewezen reserveringsaanvraag" />;

        case 'PENDING':
            return <FormattedMessage defaultMessage="Reserveringsaanvraag" />;
        case 'CANCELLED_PARTNER':
        case 'CANCELLED_EXTERNAL':
        case 'CANCELLED_GUEST':
        case 'WAIVED':
            return <FormattedMessage defaultMessage="Geannuleerde boeking" />;
        default:
            return '';
    }
};
